import { computed, nextTick, ref, unref, watch } from 'vue';

/**
 * @typedef {object} AutocompleteOption
 * @property {any} id - Unique identifier for the option
 * @property {string} name - Display name for the option
 * @property {*} [any] - Any additional properties that might be used for searching or display
 */

/**
 * Creates an autocomplete component with search and selection functionality
 * @param {object} config
 * @param {AutocompleteOption[]} config.suggestions - Array of suggestion items
 * @param {AutocompleteOption|null} config.initialValue - Initial selected value
 * @param {string} [config.compareBy] - Property name to use for comparison
 * @param {string} [config.labelBy] - Property name to use for display label
 * @param {string} [config.valueBy] - Property name to use for value
 * @param {string[]} [config.searchBy] - Array of property names to search through
 * @param {*} [config.defaultEmptyValue] - Default value when cleared
 * @param {function(AutocompleteOption): void} [config.onSelect] - Callback when an option is selected
 * @param {function(): void} [config.onOpen] - Callback when dropdown opens
 * @param {function(): void} [config.onClose] - Callback when dropdown closes
 * @param {function(): void} [config.onClear] - Callback when value is cleared
 * @param {function(string): void} [config.onTyping] - Callback when user types in search
 * @param {number} [config.debounceTime] - Debounce time for search in milliseconds
 * @param {boolean} [config.isManualFilter] - Whether to use manual filtering
 * @param {boolean} [config.isKeepOpenOnSelect] - Whether to keep dropdown open after selection
 */
export function useAutocomplete({
  suggestions,
  initialValue,
  compareBy = 'id',
  labelBy = 'name',
  valueBy = 'id',
  searchBy = ['name'],
  defaultEmptyValue = '',
  onSelect,
  onOpen,
  onClose,
  onClear,
  onTyping,
  debounceTime = 300,
  isManualFilter = false,
  isKeepOpenOnSelect = false,
}) {
  const searchElement = ref(null);
  const search = ref('');
  const isOpen = ref(false);
  const selectedValue = ref(
    JSON.parse(JSON.stringify(initialValue)) || defaultEmptyValue || null
  );
  const debounceTimeout = ref(null);

  const cursorPosition = ref(0);

  const filteredSuggestions = computed(() => {
    // If manual filter is true, return all suggestions
    if (isManualFilter) {
      return unref(suggestions);
    }

    if (!search.value) {
      return unref(suggestions);
    }

    const searchValue =
      typeof unref(search) === 'string' ? unref(search).toLowerCase() : '';

    return unref(suggestions).filter(suggestion => {
      return searchBy.some(property => {
        const value = suggestion[property];
        return (
          typeof value === 'string' && value.toLowerCase().includes(searchValue)
        );
      });
    });
  });

  const hasNoSuggestions = computed(() => {
    return filteredSuggestions.value.length === 0 && search.value.length === 0;
  });

  const hasNoSearchResults = computed(() => {
    return filteredSuggestions.value.length === 0 && search.value.length > 0;
  });

  const getLabel = computed(() => {
    if (!selectedValue.value) return JSON.stringify(selectedValue.value);

    return selectedValue.value ? selectedValue.value[labelBy] : '';
  });

  const getValue = computed(() => {
    if (!selectedValue.value) return JSON.stringify(selectedValue.value);

    return selectedValue.value ? selectedValue.value[valueBy] : '';
  });

  function isSelected(payload) {
    if (!selectedValue.value) return false;
    if (!compareBy) return payload === unref(selectedValue);

    return payload[compareBy] === unref(selectedValue)[compareBy];
  }

  function isHovered(index) {
    return cursorPosition.value === index;
  }

  function setSelectedValue(payload) {
    selectedValue.value = payload;

    if (onSelect) onSelect(payload);

    // Only close if not keeping open on select, or if this is the last remaining suggestion
    if (!isKeepOpenOnSelect || filteredSuggestions.value.length <= 1) {
      close();
    }
  }

  function syncSelectedValue(payload) {
    // If the payload is not provided, do nothing
    if (!payload) return;

    // If the payload is the same as the selected value, do nothing
    if (payload === selectedValue.value) return;

    // Set the selected value without triggering the onSelect callback
    selectedValue.value = payload;
  }

  function clear() {
    const clearValue = defaultEmptyValue || '';
    selectedValue.value = clearValue;

    if (onSelect) onSelect(clearValue);

    if (onClear) onClear(clearValue);

    nextTick(() => {
      close();
    });
  }

  function open() {
    isOpen.value = true;

    // If there is a value, and there are suggestions, set the cursor to the value
    if (getValue.value && filteredSuggestions.value.length > 0) {
      cursorPosition.value = filteredSuggestions.value.findIndex(
        suggestion => suggestion[valueBy] === getValue.value
      );
    }

    nextTick(() => {
      if (onOpen) onOpen();
    });
  }

  function close() {
    isOpen.value = false;

    if (onClose) onClose();
  }

  function setCursorPosition(index) {
    cursorPosition.value = index;
  }

  function onArrowDown() {
    if (filteredSuggestions.value.length === 0) return;

    const isLastItem =
      cursorPosition.value === filteredSuggestions.value.length - 1;

    if (!isLastItem) cursorPosition.value++;
  }

  function onArrowUp() {
    if (filteredSuggestions.value.length === 0) return;

    const isFirstItem = cursorPosition.value === 0;
    if (!isFirstItem) cursorPosition.value--;
  }

  function onEnter() {
    if (filteredSuggestions.value.length === 0) return;

    const currentItem = filteredSuggestions.value[cursorPosition.value];
    if (currentItem) setSelectedValue(currentItem);
  }

  watch(search, value => {
    if (!onTyping) return;

    if (!isManualFilter) {
      // Call immediately without debounce
      onTyping(value);

      // Reset cursor position
      cursorPosition.value = 0;
      return;
    }

    // Use debounce only when isManualFilter is true
    clearTimeout(debounceTimeout.value);
    debounceTimeout.value = setTimeout(() => {
      onTyping(value);

      // Reset cursor position
      cursorPosition.value = 0;
    }, debounceTime);
  });

  watch(
    () => initialValue,
    value => {
      selectedValue.value = value;
    }
  );

  return {
    close,
    hasNoSearchResults,
    hasNoSuggestions,
    isOpen,
    isSelected,
    isHovered,
    open,
    search,
    clear,
    setSelectedValue,
    syncSelectedValue,
    suggestions: filteredSuggestions,
    value: selectedValue,
    getLabel,
    getValue,
    searchElement,
    onArrowDown,
    onArrowUp,
    onEnter,
    setCursorPosition,
  };
}
